import { useId, useRef } from "react";
import { brand } from "../../../config/theme";
import { POINTER_MAX_PX, POINTER_MIN_PX, modelTopUrl, pointerLengthPx, type VehicleModel } from "../../../config/vehicleModels";

// Puntero estilo Tesla/Uber: la vista cenital del vehículo gira con el rumbo y
// el estado es LUZ, no cajas ni óvalos de línea (se veían genéricos): un halo
// en el piso que respira lento, del color de su estado, en TODOS los estados,
// más lo propio de cada uno:
// - en ruta: estela detrás que se alarga con la velocidad (se lee que circula
//   y hacia dónde);
// - detenida: luces de freno encendidas atrás;
// - sin señal: vehículo en gris y atenuado, halo gris tenue.
// Seleccionada: el halo más amplio e intenso (sin aro: Daniel, 6-oct-2026).
const STATUS: Record<string, string> = {
  driving: brand.primary,
  ralenti: "#F09C0C",
  stopped: "#E84243",
  offline: "#8A93A3",
};

// Mismo largo que la animación de zoom de Google (~250-300 ms): el vehículo
// crece y se achica JUNTO con el mapa, no después.
const SCALE_MS = 280;

interface Props {
  model: VehicleModel;
  status: string;
  course: number;
  /** km/h en vivo; alarga la estela. */
  speed: number | null;
  focused: boolean;
  zoom: number;
  lat: number;
  scaleFactor?: number;
  /** Recibe cuánto mide hacia arriba ya girado, para poner la etiqueta encima. */
  children?: (halfHeight: number) => React.ReactNode;
}

export default function VehiclePointer({ model, status, course, speed, focused, zoom, lat, scaleFactor = 1, children }: Props) {
  const uid = useId().replace(/:/g, "");
  // Se dibuja UNA vez al tamaño máximo y se escala con transform (GPU), así
  // el cambio de zoom anima en vez de re-dibujar a otro tamaño de golpe.
  const H = POINTER_MAX_PX;
  const W = H / model.aspect;
  const baseLength = pointerLengthPx(model.lengthM, zoom, lat);
  const length = scaleFactor < 1 ? baseLength * scaleFactor : Math.max(baseLength, POINTER_MIN_PX * scaleFactor);
  const scale = Math.min(POINTER_MAX_PX, length) / H;
  const color = STATUS[status] ?? STATUS.offline;

  // Giro por el camino corto: de 350° a 10° son +20°, no −340°.
  const angleRef = useRef<number | null>(null);
  const target = Number.isFinite(course) ? course : 0;
  if (angleRef.current == null) angleRef.current = target;
  else angleRef.current += ((target - angleRef.current + 540) % 360) - 180;
  const angle = angleRef.current;

  // Estela: de 40% del largo (despacio) a 120% (≥ 100 km/h). Con 25-90% y el
  // mismo difuminado del halo casi no se veía.
  const moving = status === "driving" && (speed ?? 0) >= 3;
  const wake = H * Math.min(1.2, 0.4 + ((speed ?? 0) / 100) * 0.8);

  const gx = W * 0.85; // halo: un poco más ancho que el vehículo
  const gy = H * 0.6;
  const box = Math.ceil(Math.max(gx, gy + (moving ? wake * 0.5 : 0)) * 2 + 16);
  const rad = (angle * Math.PI) / 180;
  const halfHeight = (scale * (Math.abs(H * Math.cos(rad)) + Math.abs(W * Math.sin(rad)))) / 2;
  const blur = W * 0.28;

  return (
    // El cuadro es del tamaño máximo pero no captura clics: sólo el vehículo.
    <div className="relative pointer-events-none" style={{ width: box, height: box }}>
      {children?.(halfHeight)}
      <div
        className="absolute inset-0 motion-reduce:transition-none"
        // --giro: en una curva, lo que falta por girar (lo pone MapView por cuadro).
        style={{ transform: `rotate(calc(${angle}deg + var(--giro, 0deg))) scale(${scale})`, transition: `transform ${SCALE_MS}ms ease-out` }}
      >
        <svg width={box} height={box} viewBox={`${-box / 2} ${-box / 2} ${box} ${box}`} className="absolute inset-0 overflow-visible">
          <defs>
            <filter id={`b${uid}`} x="-60%" y="-60%" width="220%" height="220%">
              <feGaussianBlur stdDeviation={blur} />
            </filter>
            <filter id={`s${uid}`} x="-60%" y="-30%" width="220%" height="160%">
              <feGaussianBlur stdDeviation={blur * 0.35} />
            </filter>
            <linearGradient id={`w${uid}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor={color} stopOpacity="0.8" />
              <stop offset="0.35" stopColor={color} stopOpacity="0.45" />
              <stop offset="1" stopColor={color} stopOpacity="0" />
            </linearGradient>
          </defs>

          {/* Estela detrás (el frente del dibujo apunta hacia arriba, -y). */}
          {moving && (
            <path
              d={`M${-W * 0.46} ${H * 0.36} Q0 ${H * 0.44} ${W * 0.46} ${H * 0.36} L${W * 0.08} ${H * 0.4 + wake} Q0 ${H * 0.41 + wake} ${-W * 0.08} ${H * 0.4 + wake} Z`}
              fill={`url(#w${uid})`}
              filter={`url(#s${uid})`}
            />
          )}

          {/* Halo en el piso: respira lento en todos los estados. */}
          <ellipse
            cx="0"
            cy="0"
            rx={gx * (focused ? 1.15 : 1)}
            ry={gy * (focused ? 1.12 : 1)}
            fill={color}
            opacity={(status === "offline" ? 0.22 : 0.4) + (focused ? 0.18 : 0)}
            filter={`url(#b${uid})`}
            className="motion-safe:animate-breathe"
            style={{ transformBox: "fill-box", transformOrigin: "center" }}
          />

          {/* Área táctil mínima de 32 px en pantalla: de lejos el vehículo
              mide 12 px y atinarle era tedioso. Invisible. */}
          <circle cx="0" cy="0" r={16 / scale} fill="transparent" className="pointer-events-auto cursor-pointer" />
          <image
            href={modelTopUrl(model.id)}
            x={-W / 2}
            y={-H / 2}
            width={W}
            height={H}
            opacity={status === "offline" ? 0.55 : 1}
            style={{ ...(status === "offline" ? { filter: "grayscale(1)" } : {}), ...(model.pixel ? { imageRendering: "pixelated" } : {}) }}
            preserveAspectRatio="xMidYMid meet"
            className="pointer-events-auto cursor-pointer"
          />

          {/* Luces de freno: dos puntos rojos que brillan atrás. */}
          {status === "stopped" && (
            <g filter={`url(#b${uid})`}>
              {[-1, 1].map((sx) => (
                <ellipse key={sx} cx={sx * W * 0.3} cy={H * 0.47} rx={W * 0.16} ry={H * 0.035} fill="#FF3B30" opacity="0.95" />
              ))}
            </g>
          )}
          {status === "stopped" &&
            [-1, 1].map((sx) => (
              <ellipse key={`c${sx}`} cx={sx * W * 0.3} cy={H * 0.47} rx={W * 0.09} ry={H * 0.018} fill="#FF6B5E" />
            ))}

        </svg>
      </div>
    </div>
  );
}
